import type { RumInitConfiguration } from '@datadog/browser-rum'

import { type PreferredLanguage, preferredLanguages } from '~/data/types'

import getDatadogConfig from './datadog'

export type BackendKey = 'gamma' | 'prod' | 'prod_eu'
export type BuildProfile = 'preview' | 'gamma' | 'prod' | 'prod_eu'

type EnvironmentType = 'production' | 'staging' | 'development'

type BackendBundle = {
  apiUrl: string
  serviceUrls: {
    /**
     * The dedicated `Synop Assistant API` gateway (api-manager `assistant-api.ts`) fronting assistant-api, the
     * service behind the assistant chat surface that the `assistant-v2` gate turns on.
     */
    assistant: string
    billing: string
    chargers: string
    ders: string
    derSimulator: string
    /**
     * The dedicated `Synop Fleet API` gateway (api-manager `fleet-api.ts`): the fleet-management routes
     * added for the vehicle catalog, efficiency and charge models, plus vehicle-intelligence. Every other
     * fleet-management route stays on `fleetManagement`. The `fleet-gateway` client uses it as its base
     * URL. Empty where the gateway is not deployed yet.
     */
    fleet: string
    fleetManagement: string
    reports: string
    /**
     * ticketing-api, the dedicated backend for the ticketing routes (tickets, projects, plans, templates, assets,
     * attachments, documents, email intake), served to the `ticketing` client. Until ticketing-api is deployed
     * behind its own gateway this points at the shared gateway as a placeholder.
     */
    ticketing: string
    vehicleSimulator: string
    workflows: string
  }
  publicApiUrl: string
  ocppWebsocketUrl: string
  /**
   * The live-data WebSocket service's origin (`wss://…`). Empty in backends
   * where the service is not deployed — a consumer then reports live data as
   * unavailable rather than opening a socket.
   */
  liveDataUrl: string
  datadog: RumInitConfiguration | null
  auth0: {
    domain: string
    clientId: string
  }
  statsigClientKey: string
}

export type Config = {
  appVersion: string
  buildTimestamp: string
  defaultLanguage: PreferredLanguage
  defaultLocale: string
  /**
   * The active backend the app is currently talking to. In `preview` builds
   * this is read from `localStorage` and may differ from `defaultBackend`. In
   * all other build profiles it equals the build profile.
   */
  environment: BackendKey
  environmentType: EnvironmentType
  rollbarClientAccessToken: string
  rollbarEnv: string
  muiXLicenseKey: string
  supportedBrowsersRegex?: RegExp
} & BackendBundle

export const BACKEND_KEYS: BackendKey[] = ['gamma', 'prod', 'prod_eu']
export const BACKEND_STORAGE_KEY = 'synop_backend'
export const BACKEND_QUERY_PARAM = 'backend'

const VALID_BUILD_PROFILES: BuildProfile[] = ['preview', 'gamma', 'prod', 'prod_eu']

function readBuildProfile(): BuildProfile {
  const value = process.env.NEXT_PUBLIC_BUILD_PROFILE
  if (!value) {
    throw new Error('`NEXT_PUBLIC_BUILD_PROFILE` environment variable not set. Add it to your `.env` file.')
  }
  if (!VALID_BUILD_PROFILES.includes(value as BuildProfile)) {
    throw new Error(
      `Invalid build profile: "${value}". Must be one of: ${VALID_BUILD_PROFILES.map((p) => `"${p}"`).join(', ')}. ` +
        'Check your `.env` file.'
    )
  }
  return value as BuildProfile
}

export function isBackendKey(value: unknown): value is BackendKey {
  return typeof value === 'string' && (BACKEND_KEYS as string[]).includes(value)
}

const appVersion = process.env.NEXT_PUBLIC_APP_VERSION || ''
const buildTimestamp = process.env.NEXT_PUBLIC_BUILD_TIMESTAMP || ''

export const buildProfile: BuildProfile = readBuildProfile()
export const defaultBackend: BackendKey = buildProfile === 'preview' ? 'gamma' : buildProfile
/**
 * Whether the build allows the user to switch backends at runtime. True only
 * for the `preview` profile; never true in `gamma`, `prod`, or `prod_eu`.
 */
export const allowsRuntimeBackendSwitch = buildProfile === 'preview'

type ServiceUrls = BackendBundle['serviceUrls']

/**
 * Apply the local-development override to a backend's live-data WebSocket URL.
 * Set `NEXT_PUBLIC_LIVE_DATA_URL_OVERRIDE` in `.env.local` to point the live
 * data client at a locally-running service (e.g. `ws://localhost:8080`). The
 * override also fills in for a backend whose baseline URL is empty.
 */
function applyLiveDataUrlOverride(base: string): string {
  const override = process.env.NEXT_PUBLIC_LIVE_DATA_URL_OVERRIDE
  return override ? override.replace(/\/$/, '') : base
}

/**
 * Apply local-development overrides to a backend's baseline service URLs.
 * Each entry pairs a service key with the `NEXT_PUBLIC_<SERVICE>_API_URL_OVERRIDE`
 * env var that overrides it — set the matching env var in `.env.local` to
 * route that service at a locally-running backend (e.g.
 * `NEXT_PUBLIC_DERS_API_URL_OVERRIDE=http://localhost:8000`). Only the
 * per-service client whose key is overridden picks it up; the root (legacy
 * all-in-one) client and the other per-service clients continue to hit the
 * active backend's gateway URLs.
 */
function applyUrlOverrides(base: ServiceUrls): ServiceUrls {
  const pick = (override: string | undefined) => (override ? override.replace(/\/$/, '') : undefined)
  return {
    assistant: pick(process.env.NEXT_PUBLIC_ASSISTANT_API_URL_OVERRIDE) ?? base.assistant,
    billing: pick(process.env.NEXT_PUBLIC_BILLING_API_URL_OVERRIDE) ?? base.billing,
    chargers: pick(process.env.NEXT_PUBLIC_CHARGERS_API_URL_OVERRIDE) ?? base.chargers,
    ders: pick(process.env.NEXT_PUBLIC_DERS_API_URL_OVERRIDE) ?? base.ders,
    derSimulator: pick(process.env.NEXT_PUBLIC_DER_SIMULATOR_API_URL_OVERRIDE) ?? base.derSimulator,
    fleet: pick(process.env.NEXT_PUBLIC_FLEET_API_URL_OVERRIDE) ?? base.fleet,
    fleetManagement: pick(process.env.NEXT_PUBLIC_FLEET_MANAGEMENT_API_URL_OVERRIDE) ?? base.fleetManagement,
    reports: pick(process.env.NEXT_PUBLIC_REPORTS_API_URL_OVERRIDE) ?? base.reports,
    ticketing:
      pick(process.env.NEXT_PUBLIC_TICKETING_API_URL_OVERRIDE) ??
      (process.env.NODE_ENV === 'development' ? 'http://localhost:8081' : base.ticketing),
    vehicleSimulator: pick(process.env.NEXT_PUBLIC_VEHICLE_SIMULATOR_API_URL_OVERRIDE) ?? base.vehicleSimulator,
    workflows: pick(process.env.NEXT_PUBLIC_WORKFLOWS_API_URL_OVERRIDE) ?? base.workflows
  }
}

const backendConfigs: Record<BackendKey, BackendBundle> = {
  gamma: {
    apiUrl: 'https://iehhv31e6a.execute-api.us-east-1.amazonaws.com/gamma',
    serviceUrls: applyUrlOverrides({
      // assistant-api's dedicated JWT gateway (api-manager `assistant-api`).
      assistant: 'https://mn093ibcz2.execute-api.us-east-1.amazonaws.com/gamma',
      // Billing has its own dedicated internal gateway (like ders): the `Synop Billing API` gateway.
      billing: 'https://9cyu66esz0.execute-api.us-east-1.amazonaws.com/gamma',
      chargers: 'https://iehhv31e6a.execute-api.us-east-1.amazonaws.com/gamma',
      ders: 'https://plq9dq9tuh.execute-api.us-east-1.amazonaws.com/gamma',
      // der-simulator has its own dedicated gateway (the `Synop DER Simulator
      // API` gateway, like ders/billing); its dual authorizer accepts the
      // logged-in user's Bearer token.
      derSimulator: 'https://m9hj9ymau3.execute-api.us-east-1.amazonaws.com/gamma',
      // The new fleet-management routes have their own gateway (the `Synop Fleet API` gateway); the
      // shared gateway is at its resources-per-API quota.
      fleet: 'https://87fsnjy5ac.execute-api.us-east-1.amazonaws.com/gamma',
      fleetManagement: 'https://iehhv31e6a.execute-api.us-east-1.amazonaws.com/gamma',
      reports: 'https://iehhv31e6a.execute-api.us-east-1.amazonaws.com/gamma',
      // ticketing-api's dedicated JWT gateway (api-manager `ticketing-api`), fronting the ticketing-api service.
      ticketing: 'https://f2xst91mjg.execute-api.us-east-1.amazonaws.com/gamma',
      vehicleSimulator: 'https://iehhv31e6a.execute-api.us-east-1.amazonaws.com/gamma',
      // Workflows has its own dedicated internal gateway (like ders), not the
      // shared one above. Live in gamma, prod-us, and prod-eu.
      workflows: 'https://3nxc9zoeyd.execute-api.us-east-1.amazonaws.com/gamma'
    }),
    publicApiUrl: 'https://w8ao70hcl4.execute-api.us-east-1.amazonaws.com/gamma',
    datadog: getDatadogConfig('gamma', appVersion),
    ocppWebsocketUrl: 'chargers.gamma.synop.ai/charger',
    liveDataUrl: applyLiveDataUrlOverride('wss://live.gamma.synop.ai'),
    auth0: {
      domain: 'auth0.gamma.synop.ai',
      clientId: 'BprTgaUUrEuvnBP8K30PUsv7VB4LIFFA'
    },
    statsigClientKey: 'client-eJpK5lTEDBfbXIr7M0vcnW5qBjYrR7mn9nNk0flOQOx'
  },
  prod: {
    apiUrl: 'https://2ufjtpakn2.execute-api.us-east-1.amazonaws.com/prod',
    serviceUrls: applyUrlOverrides({
      // assistant-api's dedicated JWT gateway (api-manager `assistant-api`).
      assistant: 'https://sz8wza5r50.execute-api.us-east-1.amazonaws.com/prod',
      // Billing has its own dedicated internal gateway (like ders): the `Synop Billing API` gateway.
      billing: 'https://o1srgiazo3.execute-api.us-east-1.amazonaws.com/prod',
      chargers: 'https://2ufjtpakn2.execute-api.us-east-1.amazonaws.com/prod',
      ders: 'https://gnav9renta.execute-api.us-east-1.amazonaws.com/prod',
      // der-simulator has its own dedicated internal gateway (the `Synop DER
      // Simulator API` gateway, like ders/billing); prod-us is live (api-manager
      // stage `prod`). Only used behind the solar-sim-management gate.
      derSimulator: 'https://5epkfxj8xi.execute-api.us-east-1.amazonaws.com/prod',
      // The `Synop Fleet API` gateway (api-manager `fleet-api`, stage `prod`).
      fleet: 'https://b1xhge9o3i.execute-api.us-east-1.amazonaws.com/prod',
      fleetManagement: 'https://2ufjtpakn2.execute-api.us-east-1.amazonaws.com/prod',
      reports: 'https://2ufjtpakn2.execute-api.us-east-1.amazonaws.com/prod',
      // ticketing-api's dedicated JWT gateway (api-manager `ticketing-api`), fronting the ticketing-api service.
      ticketing: 'https://79yb2gtl3a.execute-api.us-east-1.amazonaws.com/prod',
      vehicleSimulator: 'https://2ufjtpakn2.execute-api.us-east-1.amazonaws.com/prod',
      // Workflows has its own dedicated internal gateway (like ders), not the
      // shared one above. prod-us is live (api-manager stage `prod`).
      workflows: 'https://t0uh34ktv3.execute-api.us-east-1.amazonaws.com/prod'
    }),
    publicApiUrl: 'https://0czz617e0d.execute-api.us-east-1.amazonaws.com/prod',
    datadog: getDatadogConfig('prod', appVersion),
    ocppWebsocketUrl: 'chargers.synop.ai/charger',
    // The live-data stage's own execute-api URL; prod has no custom domain.
    liveDataUrl: applyLiveDataUrlOverride('wss://dtbtod4y9a.execute-api.us-east-1.amazonaws.com/prod'),
    auth0: {
      domain: 'auth.synop.ai',
      clientId: 'aiFI5MWtxMQ0r5MzTJ8Wp8BfKJ5zlbO6'
    },
    statsigClientKey: 'client-eJpK5lTEDBfbXIr7M0vcnW5qBjYrR7mn9nNk0flOQOx'
  },
  prod_eu: {
    apiUrl: 'https://w1fcqlqbg5.execute-api.eu-west-1.amazonaws.com/prod-eu',
    serviceUrls: applyUrlOverrides({
      // assistant-api's dedicated JWT gateway (api-manager `assistant-api`).
      assistant: 'https://yz11j2l0ab.execute-api.eu-west-1.amazonaws.com/prod-eu',
      // Billing has its own dedicated internal gateway (like ders): the `Synop Billing API` gateway.
      billing: 'https://8rw3abyb27.execute-api.eu-west-1.amazonaws.com/prod-eu',
      chargers: 'https://w1fcqlqbg5.execute-api.eu-west-1.amazonaws.com/prod-eu',
      ders: 'https://nv0fzi6dl7.execute-api.eu-west-1.amazonaws.com/prod-eu',
      // der-simulator has no prod-eu gateway yet: fill this in once prod-EU gets
      // its derSimulatorAPI VPC link + stage vars in api-manager.
      derSimulator: '',
      // The `Synop Fleet API` gateway (api-manager `fleet-api`, stage `prod-eu`).
      fleet: 'https://6squgv2tzh.execute-api.eu-west-1.amazonaws.com/prod-eu',
      fleetManagement: 'https://w1fcqlqbg5.execute-api.eu-west-1.amazonaws.com/prod-eu',
      reports: 'https://w1fcqlqbg5.execute-api.eu-west-1.amazonaws.com/prod-eu',
      // ticketing-api's dedicated JWT gateway (api-manager `ticketing-api`), fronting the ticketing-api service.
      ticketing: 'https://dsrpl09ref.execute-api.eu-west-1.amazonaws.com/prod-eu',
      vehicleSimulator: 'https://w1fcqlqbg5.execute-api.eu-west-1.amazonaws.com/prod-eu',
      // Workflows has its own dedicated internal gateway (like ders): the
      // `Synop Workflows API` gateway. prod-eu is live (api-manager stage `prod-eu`).
      workflows: 'https://lmyqtutruh.execute-api.eu-west-1.amazonaws.com/prod-eu'
    }),
    publicApiUrl: 'https://k0qtc4au72.execute-api.eu-west-1.amazonaws.com/prod-eu',
    datadog: getDatadogConfig('prod_eu', appVersion),
    ocppWebsocketUrl: 'chargers.eu.synop.ai/charger',
    // The live-data stage's own execute-api URL; prod-eu has no custom domain.
    liveDataUrl: applyLiveDataUrlOverride('wss://8oxyf3zzgf.execute-api.eu-west-1.amazonaws.com/prod-eu'),
    auth0: {
      domain: 'auth.eu.synop.ai',
      clientId: 'tBsLRmyieEolxSBeZhxPnuMff5K2bxIS'
    },
    statsigClientKey: 'client-eJpK5lTEDBfbXIr7M0vcnW5qBjYrR7mn9nNk0flOQOx'
  }
}

/**
 * Resolves the active backend on app startup. Only consults `localStorage`
 * when running in the `preview` build profile — other profiles always return
 * the build's hard-coded default. Pure read; never writes. The `?backend=`
 * query param and "first-visit write the default" handling live in
 * `BackendBootstrap`, which runs as a client effect and triggers a reload via
 * `switchBackend` if the URL disagrees with what is already persisted.
 */
function resolveActiveBackend(): BackendKey {
  if (!allowsRuntimeBackendSwitch) return defaultBackend
  if (typeof window === 'undefined') return defaultBackend

  try {
    const stored = window.localStorage.getItem(BACKEND_STORAGE_KEY)
    if (isBackendKey(stored)) return stored
  } catch {
    // localStorage unavailable — fall through to default
  }

  return defaultBackend
}

export const activeBackend: BackendKey = resolveActiveBackend()

function validateDefaultLanguage(language?: string) {
  return preferredLanguages.includes(language as PreferredLanguage) ? (language as PreferredLanguage) : 'en'
}

function getEnvironmentType() {
  const environmentType = process.env.NEXT_PUBLIC_ENV_TYPE || ''
  return ['production', 'staging', 'development'].includes(environmentType)
    ? (environmentType as EnvironmentType)
    : 'production'
}

function compileSupportedBrowsersRegex() {
  const supportedBrowsers = process.env.NEXT_PUBLIC_SUPPORTED_BROWSERS_REGEX
  if (!supportedBrowsers) return undefined

  try {
    return new RegExp(supportedBrowsers)
  } catch (e) {
    return undefined
  }
}

const baseConfig = backendConfigs[activeBackend]

const envConfig: Config = {
  appVersion,
  buildTimestamp,
  defaultLanguage: validateDefaultLanguage(process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE),
  defaultLocale: process.env.NEXT_PUBLIC_DEFAULT_LOCALE || 'en-US',
  environment: activeBackend,
  environmentType: getEnvironmentType(),
  muiXLicenseKey: process.env.NEXT_PUBLIC_MUI_X_LICENSE_KEY || '',
  rollbarClientAccessToken: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_ACCESS_TOKEN || '',
  rollbarEnv: process.env.NEXT_PUBLIC_ROLLBAR_ENVIRONMENT || '',
  supportedBrowsersRegex: compileSupportedBrowsersRegex(),
  ...baseConfig
}

export default envConfig
